import { createRef, useEffect } from 'react';
import { COLORS, FONT_SIZES, HEIGHTS, SPACINGS } from 'shared/styles/variables';
import styled, { createGlobalStyle } from 'styled-components';

import SearchIcon from 'images/icons/search.svg';

const SEARCH_BAR_WIDTH = 400;

const SearchBarContainer = styled('div')`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  height: ${HEIGHTS.input};
  width: ${SEARCH_BAR_WIDTH}px;
  margin: ${SPACINGS.base * 1.5}px;
  background-color: ${COLORS.white};
  border-radius: 3px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
`;

const StyledInput = styled('input')`
  position: absolute;
  left: 0px;
  padding-left: ${HEIGHTS.input};
  width: 100%;
  height: 100%;
  font-size: ${FONT_SIZES.input}px;
  border-radius: 3px;
`;

const SearchIconContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${SPACINGS.base * 1.5}px;
  z-index: 1;
`;

const GoogleSearchBoxStyles = createGlobalStyle`
  .pac-icon {
    display: none;
  }

  .pac-item {
    display: flex;
    align-items: center;
    padding: ${SPACINGS.base}px;
    height: ${HEIGHTS.input}px;
    background-color: ${COLORS.white};
    font-size: ${FONT_SIZES.xs};

    .pac-item-query {
      font-size: ${FONT_SIZES.small};
    }
  }

  .pac-item:hover {
    cursor: pointer;
    background-color: ${COLORS.indigo10};

    .pac-item-query {
      font-size: ${FONT_SIZES.small};
      color: ${COLORS.indigo80};
    }
  }

  .pac-item-selected {
    background-color: ${COLORS.indigo10};

    .pac-item-query {
      font-size: ${FONT_SIZES.small};
      color: ${COLORS.indigo80};
    }
  }

  .pac-container {
    position: absolute;
    margin-top: ${SPACINGS.base * 0.5}px;
    border-radius: 3px;
  }
`;

interface SearchBoxProps {
  dataTest?: string;
  placeholder: string;
  onPlacesChanged: (places: google.maps.places.PlaceResult[] | undefined) => void;
}

const SearchBox = ({ dataTest, placeholder, onPlacesChanged }: SearchBoxProps) => {
  const searchInputRef = createRef<HTMLInputElement>();

  useEffect(() => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const searchBox = new google.maps.places.SearchBox(searchInputRef.current);
    const placesChangedListener = searchBox.addListener('places_changed', () =>
      onPlacesChanged(searchBox.getPlaces()),
    );

    return () => {
      google.maps.event.removeListener(placesChangedListener);
    };
  }, []);

  return (
    <SearchBarContainer>
      <GoogleSearchBoxStyles />
      <SearchIconContainer>
        <SearchIcon />
      </SearchIconContainer>
      <StyledInput ref={searchInputRef} placeholder={placeholder} data-test={dataTest} />
    </SearchBarContainer>
  );
};

export default SearchBox;
